import { escapeHtml } from '../util/escapeHtml.js';
/**
 * Player Awards — the "you have been granted something" pop-up.
 *
 * A global watcher (mountAwardWatcher, called once from main.ts) polls the
 * player's pending awards on a light interval. When one is waiting, a modal
 * appears wherever they are with the award's message and reward lines, and a
 * single "Accept" button that applies the reward. Reusable for any future
 * individually-granted award — the server decides the title, body and payload.
 */
import { api } from '../api/client.js';
import { store } from '../state/store.js';
import { showToast } from '../util/toast.js';
import type { PendingAwardDto } from '@rex-mortis/shared';

const POLL_MS = 30_000;

let mounted = false;
let pollHandle: number | null = null;
let showing = false;

export function mountAwardWatcher(): void {
  if (mounted) return;
  mounted = true;

  const poll = async (): Promise<void> => {
    if (!store.get().character || showing) return;
    try {
      const { pendingAwards } = await api.pendingAwards();
      if (pendingAwards.length > 0) showAwardModal(pendingAwards[0]!);
    } catch {
      /* best-effort — the watcher must never throw */
    }
  };

  void poll();
  pollHandle = window.setInterval(() => void poll(), POLL_MS);
}

export function stopAwardWatcher(): void {
  if (pollHandle !== null) { window.clearInterval(pollHandle); pollHandle = null; }
  mounted = false;
}

function showAwardModal(award: PendingAwardDto): void {
  if (showing) return;
  showing = true;

  document.getElementById('award-modal')?.remove();
  document.getElementById('award-overlay')?.remove();

  if (!document.getElementById('award-styles')) {
    const style = document.createElement('style');
    style.id = 'award-styles';
    style.textContent = AWARD_STYLES;
    document.head.append(style);
  }

  const overlay = document.createElement('div');
  overlay.id = 'award-overlay';
  overlay.className = 'award-overlay';
  document.body.append(overlay);

  const modal = document.createElement('div');
  modal.id = 'award-modal';
  modal.className = 'award-modal';
  const rewardsHtml = award.rewards.length
    ? `<ul class="award-rewards">${award.rewards.map((r) => `<li>${escapeHtml(r)}</li>`).join('')}</ul>`
    : '';
  modal.innerHTML = `
    <div class="award-seal">✦</div>
    <h2 class="award-title">${escapeHtml(award.title)}</h2>
    <p class="award-body">${escapeHtml(award.body)}</p>
    ${rewardsHtml}
    <div class="award-actions">
      <button class="btn" id="award-accept">Accept</button>
    </div>
  `;
  document.body.append(modal);

  const close = (): void => {
    overlay.remove();
    modal.remove();
    showing = false;
  };

  const acceptBtn = modal.querySelector<HTMLButtonElement>('#award-accept')!;
  acceptBtn.addEventListener('click', async () => {
    acceptBtn.disabled = true;
    acceptBtn.textContent = 'Claiming…';
    try {
      const res = await api.acceptAward(award.id);
      // Refresh character/bars from the server (updates the sidebar maxes).
      await api.me().catch(() => {});
      showToast(`Award claimed — ${res.rewards.join(', ') || 'granted'}.`, 'success');
      close();
    } catch {
      acceptBtn.disabled = false;
      acceptBtn.textContent = 'Accept';
      showToast('Could not claim the award. Try again.', 'danger');
    }
  });
}

const AWARD_STYLES = `
  .award-overlay {
    position: fixed; inset: 0; z-index: 400;
    background: rgba(10, 7, 2, 0.82); backdrop-filter: blur(3px);
  }
  .award-modal {
    position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: min(540px, 94vw); max-height: 92vh; overflow-y: auto; z-index: 401;
    background: var(--c-parchment); background-image: var(--parchment-texture);
    border: 2px solid var(--c-gold, #b8860b);
    box-shadow: 0 0 0 1px var(--c-gold, #b8860b), 0 0 50px rgba(184, 134, 11, 0.35), 0 24px 70px rgba(0, 0, 0, 0.7);
    border-radius: var(--radius-lg); padding: var(--s-6); text-align: center;
  }
  .award-seal {
    font-size: 3rem; line-height: 1; color: var(--c-gold, #b8860b);
    margin-bottom: var(--s-2); text-shadow: 0 0 20px rgba(184, 134, 11, 0.5);
  }
  .award-title {
    font-family: var(--font-heading); text-transform: uppercase; letter-spacing: 0.06em;
    color: var(--c-oxblood); margin: 0 0 var(--s-3) 0; font-size: 1.6rem;
  }
  .award-body { color: var(--c-ink); line-height: 1.6; margin: 0 auto var(--s-4); max-width: 460px; }
  .award-rewards {
    list-style: none; padding: var(--s-3); margin: 0 auto var(--s-4); max-width: 320px;
    border: 1px solid var(--c-gold, #b8860b); border-radius: var(--radius);
    background: rgba(184, 134, 11, 0.08);
  }
  .award-rewards li {
    font-family: var(--font-numeric); color: var(--c-ink); font-weight: 600;
    padding: 2px 0;
  }
  .award-actions { display: flex; justify-content: center; }
`;
